import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import apiClient from '@/lib/api/client';

export function BugReportButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Only show on Genius pages
  if (!location.pathname.startsWith('/genius/')) return null;

  const handleSubmit = async () => {
    if (description.trim().length < 10) {
      toast.error('Please describe the bug in at least 10 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post('/bug-reports', {
        description: description.trim(),
        page_url: window.location.href,
      });
      toast.success('Bug report submitted — thank you!');
      setDescription('');
      setOpen(false);
    } catch {
      toast.error('Failed to submit bug report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <button className="h-10 w-10 rounded-full border-2 border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors">
              <Bug className="h-4 w-4 text-slate-500" />
            </button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Report a bug</TooltipContent>
      </Tooltip>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5" />
            Report a Bug
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Describe what happened and what you expected. We'll look into it right away.
            </p>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What went wrong? What were you trying to do?"
              className="resize-none h-32"
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground text-right mt-1">{description.length}/2000</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Page: {typeof window !== 'undefined' ? window.location.pathname : ''}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting || description.trim().length < 10}
            >
              {isSubmitting ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5 mr-1.5" />
              )}
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
